import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withTheme, ThemeProvider } from 'emotion-theming';

import FlexboxGridThemes from '../../styling/themes/FlexboxGridThemes';
import SlatThemes from '../../styling/themes/SlatThemes';
import LandingPageThemes from '../../styling/themes/LandingPageThemes';

import BaseSlat from './BaseSlat';
import BaseMediaContainer from './SlatComponents/BaseMediaContainer';
import BaseTextContainer from './SlatComponents/BaseTextContainer';

const MAX_COLUMN_WIDTH = 12;
const DEFAULT_COLUMN_WIDTH = 6;

/**
 * Slat
 * @description
 * This component is wrapper for most banner like content on landing pages. It will render its
 * children with the given layout.
 *
 *
 * @property {string} backgroundColor
 * @property {string} backgroundImage
 * @property {string} className
 * @property {string} color - Color of text within the component
 * @property {string} media - object contains urls for either video or image to put in slat
 * @property {string} id - html id used for linking to component on the page
 * @property {string} type - id of the theme that we will use for this slat
 * @property {string} height - Height of slat, (auto, 240, 320, 480, 560, 640)
 * @property {string} layout - Positioning of the text content in the slat (left, right, center)
 * @property {string} alignment - Alignment of the text content within the slat on desktop (left, center)
 * @property {string} mobileAlignment - Alignment of the text content within the slat on mobile (left, cmobileAlignmententer)
 *
 * @example
 *  <Slat
 *    backgroundColor="green"
 *    backgroundImage={backgroundImage}
 *    color="white"
 *    height="560px"
 *    media={mediaObject}
 *    layout="left"
 *   >
 *    <Textbox prehead={prehead} heading={heading} dek={dek} />
 *    <Button color="lapel" type="OutlineButton">Click me!</Button>
 *  </Slat>
 */

class SimpleSlat extends PureComponent {
  static propTypes = {
    backgroundColor: PropTypes.string,
    backgroundImage: PropTypes.string,
    className: PropTypes.string,
    color: PropTypes.string,
    imageSize: PropTypes.string,
    id: PropTypes.string,
    type: PropTypes.string,
    height: PropTypes.oneOf([
      'auto',
      '240px',
      '300px',
      '320px',
      '400px',
      '410px',
      '420px',
      '480px',
      '520px',
      '560px',
      '600px',
      '640px',
    ]),
    layout: PropTypes.oneOf(['left', 'right', 'center', 'custom']),
    mobileLayout: PropTypes.oneOf(['row', 'columnReverse']),
    alignment: PropTypes.oneOf(['left', 'right', 'center']),
    mobileAlignment: PropTypes.oneOf(['left', 'right', 'center']),
    // TODO: Change this back to checking for ImageMediaObject or VideoMediaObject and not rendering if undefined
    media: PropTypes.object,
    tracking: PropTypes.object,
  };

  static defaultProps = {
    color: 'lapel',
    height: 'auto',
    imageSize: 'cover',
    layout: 'center',
    theme: LandingPageThemes.default,
  };

  render() {
    const {
      backgroundColor,
      backgroundImage,
      children,
      className,
      color,
      height,
      mobileHeight,
      header,
      id,
      type,
      media,
      imageSize,
      layout,
      mobileLayout,
      customContentStyles,
      theme,
      tracking,
    } = this.props;

    let { alignment, mobileAlignment } = this.props;

    alignment = alignment ? alignment : 'center';
    mobileAlignment = mobileAlignment ? mobileAlignment : alignment;

    // These value may change based on other props
    let { contentColumnSizing, mediaColumnSizing } = this.props;

    // If column sizing isn't defined, we'll apply defaults
    if (!contentColumnSizing) {
      if (layout === 'center') {
        // 'center'ed content appears stacked on mobile, and no media is displayed on desktop
        contentColumnSizing = { xs: MAX_COLUMN_WIDTH };
        mediaColumnSizing = { xs: MAX_COLUMN_WIDTH, sm: false };
      } else {
        // non-'center'ed is stacked on mobile and 6:6 split on desktop
        contentColumnSizing = {
          xs: MAX_COLUMN_WIDTH,
          sm: DEFAULT_COLUMN_WIDTH,
        };
        mediaColumnSizing = { xs: MAX_COLUMN_WIDTH, sm: DEFAULT_COLUMN_WIDTH };
      }
    }

    let slatThemeProps = SlatThemes[type] || SlatThemes['LandingPageSlat'];
    let slatTheme = {
      ...FlexboxGridThemes.FlexboxGridSlatTheme,
      Slat: { ...slatThemeProps },
    };

    let mediaContainer = () => (
      <BaseMediaContainer
        mediaColumnSizing={mediaColumnSizing}
        layout={layout}
        media={media}
        imageSize={imageSize}
      />
    );

    let contentContainer = () => (
      <BaseTextContainer
        {...contentColumnSizing}
        layout={layout}
        alignment={alignment}
        mobileAlignment={mobileAlignment}
        customContentStyles={customContentStyles}
      >
        {children}
      </BaseTextContainer>
    );

    return (
      <ThemeProvider theme={slatTheme}>
        <BaseSlat
          id={id}
          className={className}
          theme={theme}
          color={color}
          backgroundColor={backgroundColor}
          backgroundImage={backgroundImage}
          header={header}
          height={height}
          mobileHeight={mobileHeight}
          layout={layout}
          mobileLayout={mobileLayout}
          media={mediaContainer}
          content={contentContainer}
          tracking={tracking}
        />
      </ThemeProvider>
    );
  }
}

export default withTheme(SimpleSlat);
